import { cn } from "../lib/cn";

/*
 * The clipboard's physical construction - pressboard board, brushed-metal
 * clip, the paper sheet resting on it - extracted so the recipe exists once.
 * Consumed by the kit's Clipboard (the modal) and by app-level standing
 * compositions (the auth screens). Internal: exposed only via the package's
 * "./internal/clipboardChrome" subpath, never from the index barrel.
 */

/**
 * How large the physical object is drawn. Not the same axis as a modal's
 * width: a scale picks the clip's jaw and the board's margins so the object
 * keeps its proportions as it shrinks - a small clipboard must still read as
 * a clipboard.
 *
 * - `sm`  ~24rem boards (small confirms)
 * - `md`  ~36rem boards
 * - `lg`  ~28-48rem boards (the auth clipboard's committed geometry)
 * - `xl`  the near-full-viewport takeover
 */
export type ClipScale = "sm" | "md" | "lg" | "xl";

/*
 * The board: pressboard, cork darkened with ink. Both ends of the mix are
 * tokens, so nothing here is an invented color - it reads as masonite sitting
 * between the espresso desk behind it and the paper sheet resting on it.
 * Edge lighting and the float over the desk share one box-shadow so two
 * shadow utilities cannot fight over the property.
 */
export const boardSurface = cn(
  "bg-[color-mix(in_srgb,var(--color-cork)_72%,var(--color-ink))]",
  "shadow-[inset_0_1px_0_rgba(255,255,255,0.16),inset_0_-2px_3px_rgba(0,0,0,0.18),var(--shadow-float)]",
);

/* Brushed metal for the clip: token base, rgba lighting on top (the LabelTape trick). */
const clipMetal = cn(
  "bg-faint",
  "bg-[linear-gradient(rgba(255,255,255,0.5),rgba(255,255,255,0.08)_45%,rgba(0,0,0,0.2))]",
);

/** The paper sheet resting on the board. Consumers add their own padding and layout. */
export const sheetSurface = "rounded-paper bg-card shadow-lift-lg";

/*
 * Board margins per scale. The top padding is the pressboard strip the clip
 * lives on; it shrinks with the clip so the strip never dwarfs the jaw.
 */
const boardFrame: Record<ClipScale, string> = {
  sm: "rounded-tag px-2 pb-2 pt-8",
  md: "rounded-tag px-2.5 pb-2.5 pt-10",
  lg: "rounded-tag px-3 pb-3 pt-11 sm:px-4 sm:pb-4",
  xl: "rounded-tag px-3 pb-3 pt-11 sm:px-4 sm:pb-4",
};

/**
 * The board's full look at a scale: surface, margins, and the `relative`
 * anchor the clip (and any board furniture) positions against. Layout - flex,
 * width caps, rotation - stays with the consumer.
 */
export function boardChrome(scale: ClipScale): string {
  return cn("relative", boardFrame[scale], boardSurface);
}

/*
 * Clip geometry per scale. `anchor` places the assembly on the board strip;
 * the hump (the rounded hinge) rises from the board's top edge and the jaw
 * straddles the strip and the sheet's top edge. The three sets of numbers
 * move together: anchor + jaw height minus the board's top padding is the
 * jaw's bite onto the paper (kept at 6-8px so the clip always visibly holds
 * the sheet), and the hump peeks 8-16px above the jaw.
 */
const clipGeometry: Record<
  ClipScale,
  { anchor: string; hump: string; jaw: string }
> = {
  sm: {
    anchor: "top-3",
    hump: "-top-3 h-5 w-10",
    jaw: "h-7 w-24 rounded-[3px]",
  },
  md: {
    anchor: "top-3.5",
    hump: "-top-3.5 h-5 w-12",
    jaw: "h-8 w-32 rounded-[4px]",
  },
  lg: {
    anchor: "top-4",
    hump: "-top-4 h-6 w-14",
    jaw: "h-9 w-40 rounded-[4px]",
  },
  xl: {
    anchor: "top-4",
    hump: "-top-4 h-6 w-16",
    jaw: "h-9 w-44 rounded-[4px]",
  },
};

/* The close button sits on the pressboard strip, so it steps down with the
   strip's height. */
const closeAnchor: Record<ClipScale, string> = {
  sm: "right-1 top-0.5 size-7",
  md: "right-1 top-1 size-8",
  lg: "right-1.5 top-1.5 size-9",
  xl: "right-1.5 top-1.5 size-9",
};

export interface ClipboardCloseButtonProps {
  scale: ClipScale;
  onClose: () => void;
}

/**
 * The board's close control: a bare X marked straight onto the pressboard,
 * top-right on the strip the clip lives on. No disc behind it - a filled
 * circle read as a sticker stuck to the board and pulled the eye off the
 * paperwork. The box keeps a comfortable hit area, only the fill is gone;
 * the stroke is the paper tone (3.4:1 on the board mix, over the 3:1 an icon
 * needs) and hover is a breath of paper light, the same treatment header
 * controls get on the desk.
 *
 * The focus ring is paper, not the accent. Indigo means Brian and owns focus
 * rings everywhere else, but `accent-lite` on this board measures 1.6:1 -
 * under the 3:1 a non-text indicator needs - and a focus ring nobody can see
 * is not a focus ring. Paper is the one tone that clears the bar here (3.4:1),
 * and it is already the ink the X is drawn in. A deliberate, board-scoped
 * exception; the accent keeps every focus ring that sits on paper.
 *
 * Render it inside a `boardChrome` container, after
 * the sheet, so it sits on the board rather than the paper. One
 * implementation for every clipboard: the kit Clipboard and the app-level
 * compositions (the search palette) share it.
 */
export function ClipboardCloseButton({
  scale,
  onClose,
}: ClipboardCloseButtonProps) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className={cn(
        "absolute inline-flex items-center justify-center rounded-full",
        closeAnchor[scale],
        "bg-transparent text-ink-inverse",
        "transition duration-150 ease-paper motion-reduce:transition-none",
        "hover:bg-paper/10",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper",
      )}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className={scale === "sm" ? "size-3.5" : "size-4"}
      >
        <path d="M4 4 12 12M12 4 4 12" />
      </svg>
    </button>
  );
}

export interface ClipboardClipProps {
  scale: ClipScale;
}

/**
 * The clip: a metal jaw with a rounded hinge hump, centered on the board's
 * pressboard strip, casting a small shadow on the paper. Decorative only
 * (aria-hidden); render it as a sibling after the sheet inside a
 * `boardChrome` container so it paints above the paper.
 */
export function ClipboardClip({ scale }: ClipboardClipProps) {
  const geometry = clipGeometry[scale];
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute left-1/2 -translate-x-1/2",
        geometry.anchor,
      )}
    >
      <div
        className={cn(
          "absolute left-1/2 -translate-x-1/2 rounded-t-full",
          geometry.hump,
          clipMetal,
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(0,0,0,0.3)]",
        )}
      />
      <div
        className={cn(
          "relative",
          geometry.jaw,
          clipMetal,
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-2px_0_rgba(0,0,0,0.25),0_3px_5px_rgba(46,35,24,0.35)]",
        )}
      />
    </div>
  );
}
